import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { SectionHeader } from '../components/SectionHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, Calendar, Trash2, MessageCircle, Send, Pencil } from 'lucide-react';
import { API, mediaUrl } from '../lib/api';
import { STAGES } from '../lib/journeyStages';
import { getAvatarImage } from '../components/Layout';
import { RichTextEditor } from '../components/RichTextEditor';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

// An image URL must be empty or start with http://, https:// or / (relative path).
const isValidImageUrl = (v) => !v || /^(https?:\/\/|\/)/.test(v);

const StoriesPage = () => {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { t, datum, datumCas } = useJazyk();
  const { markerColors } = useAppSettings();
  const { storyId } = useParams();
  const navigate = useNavigate();

  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  // Create story dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [journeyStage, setJourneyStage] = useState('none');
  const [uploading, setUploading] = useState(false);

  // Edit story dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);
  const [editJourneyStage, setEditJourneyStage] = useState('none');

  const fetchStories = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/news?category=zkusenosti`);
      setStories(r.data);
    } catch { toast.error(t('zk.chybaNacist')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  const fetchComments = useCallback(async (articleId) => {
    try {
      const r = await axios.get(`${API}/news/${articleId}/comments`);
      setComments(r.data);
    } catch { setComments([]); }
  }, []);

  // Sync the open story from the URL (:storyId) — supports direct entry / refresh / sharing.
  useEffect(() => {
    if (!storyId) { setSelected(null); setNotFound(false); return; }
    const found = stories.find(s => s.id === storyId);
    if (found) { setSelected(found); setNotFound(false); setComments([]); fetchComments(found.id); return; }
    if (stories.length > 0) {
      // Not in the loaded list — fetch the single story directly
      axios.get(`${API}/news/${storyId}`)
        .then(r => { setSelected(r.data); setNotFound(false); setComments([]); fetchComments(r.data.id); })
        .catch(() => { setSelected(null); setNotFound(true); });
    }
  }, [storyId, stories, fetchComments]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) { toast.error(t('zk.vyplnteNadpis')); return; }
    if (!imageFile && !isValidImageUrl(imageUrl)) { toast.error(t('zk.spatnyOdkaz')); return; }
    setUploading(true);
    try {
      let finalImageUrl = imageUrl;
      if (imageFile) {
        const fd = new FormData(); fd.append('file', imageFile);
        const r = await axios.post(`${API}/news/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalImageUrl = r.data.url;
      }
      await axios.post(`${API}/news`, { title, content, image_url: finalImageUrl, category: 'zkusenosti', journey_stage: journeyStage === 'none' ? '' : journeyStage });
      toast.success(t('zk.sdilen'));
      setDialogOpen(false); setTitle(''); setContent(''); setImageUrl(''); setImageFile(null); setJourneyStage('none');
      await fetchStories();
    } catch (err) { toast.error(err.response?.data?.detail || t('zk.chybaSdilet')); }
    finally { setUploading(false); }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm(t('zk.opravduSmazat'))) return;
    try {
      await axios.delete(`${API}/news/${storyId}`);
      toast.success(t('zk.smazan'));
      navigate('/zkusenosti'); await fetchStories();
    } catch { toast.error(t('pod.chybaSmazat')); }
  };

  const handleOpenEdit = (story) => {
    setEditingStory(story);
    setEditTitle(story.title || '');
    setEditContent(story.content || '');
    setEditImageUrl(story.image_url || '');
    setEditImageFile(null);
    setEditJourneyStage(story.journey_stage || 'none');
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingStory) return;
    if (!editImageFile && !isValidImageUrl(editImageUrl)) { toast.error(t('zk.spatnyOdkaz')); return; }
    setUploading(true);
    try {
      let finalImageUrl = editImageUrl;
      if (editImageFile) {
        const fd = new FormData(); fd.append('file', editImageFile);
        const r = await axios.post(`${API}/news/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        finalImageUrl = r.data.url;
      }
      const updated = await axios.put(`${API}/news/${editingStory.id}`, {
        title: editTitle, content: editContent, image_url: finalImageUrl, category: 'zkusenosti',
        journey_stage: editJourneyStage === 'none' ? '' : editJourneyStage
      });
      toast.success(t('zk.aktualizovan'));
      setEditDialogOpen(false); setEditingStory(null);
      setSelected(updated.data);
      await fetchStories();
    } catch (err) { toast.error(err.response?.data?.detail || t('pod.chybaUlozit')); }
    finally { setUploading(false); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setCommentLoading(true);
    try {
      await axios.post(`${API}/news/${selected.id}/comments`, { content: commentText });
      setCommentText('');
      await fetchComments(selected.id);
    } catch (err) { toast.error(err.response?.data?.detail || t('akt.chybaKomentar')); }
    finally { setCommentLoading(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/news/comments/${commentId}`);
      await fetchComments(selected.id);
    } catch { toast.error(t('akt.chybaSmazatKomentar')); }
  };

  const markerColor = markerColors?.stories || '#F5A9B8';
  const canCreateStory = user && user.email_verified;

  // ── Not-found view (direct entry to a missing story URL) ──────────────────
  if (notFound) {
    return (
      <div className="min-h-screen py-6" data-testid="story-not-found">
        <div className="pride-bar mb-6" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h1 className="font-serif text-xl font-semibold text-bloom-text mb-3">{t('zk.pribehNenalezen')}</h1>
          <button onClick={() => navigate('/zkusenosti')} className="inline-flex items-center gap-1.5 text-sm text-bloom-violet hover:underline">
            <ArrowLeft className="w-4 h-4" />{t('zk.zpetNaPribehy')}
          </button>
        </div>
      </div>
    );
  }

  // ── Article detail view ──────────────────────────────────────────────────
  if (selected) {
    return (
      <>
      <div className="min-h-screen py-6" data-testid="story-detail">
        <div className="pride-bar mb-6" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/zkusenosti')} className="flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />{t('zk.zpetNaPribehy')}
          </button>
          <article className="bg-white rounded-2xl border border-border/50 overflow-hidden shadow-sm" data-testid="story-article">
            {selected.image_url && (
              <img src={mediaUrl(selected.image_url)} alt={selected.title} className="w-full h-56 sm:h-72 object-cover"
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            )}
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: markerColor }} />
                <span className="text-xs text-bloom-sub font-medium">{t('nav.stories')}</span>
              </div>
              <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bloom-text mb-4 leading-tight">
                {selected.title}
              </h1>
              <div className="flex items-center gap-3 mb-6 text-xs text-bloom-sub">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={getAvatarImage(selected.admin_avatar, selected.admin_custom_avatar)} />
                  <AvatarFallback className="bg-bloom-pride-pink text-white text-[10px]">
                    {(selected.author_name || selected.admin_name || 'A').charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <Link to={selected.author_id ? `/users/${selected.author_id}` : '#'} className="font-medium hover:text-bloom-violet">
                  {selected.author_name || selected.admin_name || t('zk.komunita')}
                </Link>
                <Calendar className="w-3.5 h-3.5" />
                {datum(selected.created_at)}
              </div>
              <div className="prose prose-sm max-w-none text-bloom-text leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selected.content }} />
              {(isAdmin || (user?.id === selected.author_id)) && (
                <div className="mt-6 pt-6 border-t border-border flex gap-2">
                  <Button variant="outline" size="sm" className="border-bloom-violet/40 text-bloom-violet hover:bg-bloom-violet/10"
                    onClick={() => handleOpenEdit(selected)} data-testid="edit-story-btn">
                    <Pencil className="w-4 h-4 mr-1.5" />{t('obecne.upravit')}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(selected.id)} data-testid="delete-story-btn">
                    <Trash2 className="w-4 h-4 mr-1" />{t('obecne.smazat')}
                  </Button>
                </div>
              )}
            </div>
          </article>

          {/* Comments section */}
          <div className="mt-8" data-testid="story-comments">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-4 h-4 text-bloom-violet" />
              <h2 className="font-semibold text-bloom-text text-sm">{t('akt.komentare', { pocet: comments.length })}</h2>
            </div>

            {/* Comment list */}
            <div className="space-y-3 mb-6">
              {comments.length === 0 && (
                <p className="text-sm text-bloom-sub/70 py-3">{t('akt.zadneKomentare')}</p>
              )}
              {comments.map(c => (
                <div key={c.id} className="flex gap-3 group" data-testid={`comment-${c.id}`}>
                  <Avatar className="w-8 h-8 shrink-0">
                    <AvatarImage src={getAvatarImage(c.avatar, c.custom_avatar)} />
                    <AvatarFallback className="bg-bloom-pride-pink/30 text-bloom-pride-pink text-[10px]">
                      {c.username?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="bg-white rounded-xl border border-border/50 px-3.5 py-2.5">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Link to={`/users/${c.user_id}`} className="text-xs font-semibold text-bloom-violet hover:underline">
                          {c.username}
                        </Link>
                        <span className="text-[10px] text-bloom-sub/60">
                          {datumCas(c.created_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-bloom-text leading-relaxed">{c.content}</p>
                    </div>
                    {(isAdmin || user?.id === c.user_id) && (
                      <button onClick={() => handleDeleteComment(c.id)}
                        className="mt-1 ml-2 text-[10px] text-bloom-sub/50 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-comment-${c.id}`}>
                        Smazat
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add comment */}
            {isAuthenticated && user?.email_verified ? (
              <form onSubmit={handleAddComment} className="flex gap-2 items-start" data-testid="comment-form">
                <Avatar className="w-8 h-8 shrink-0 mt-0.5">
                  <AvatarImage src={getAvatarImage(user?.avatar, user?.custom_avatar)} />
                  <AvatarFallback className="bg-bloom-violet text-white text-[10px]">
                    {user?.username?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 flex gap-2">
                  <Input
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    placeholder={t('akt.pridatKomentar')}
                    className="flex-1"
                    data-testid="comment-input"
                  />
                  <Button type="submit" size="sm" className="bg-bloom-violet text-white shrink-0" disabled={commentLoading || !commentText.trim()}
                    data-testid="comment-submit">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            ) : isAuthenticated ? (
              <p className="text-sm text-amber-600">{t('akt.proKomentovaniOverte')} <Link to="/profile" className="font-medium hover:underline">{t('akt.profil')}</Link></p>
            ) : (
              <p className="text-sm text-bloom-sub">{t('akt.prihlasteSeProKomentovani')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onOpenChange={open => { setEditDialogOpen(open); if (!open) { setEditingStory(null); setEditTitle(''); setEditContent(''); setEditImageUrl(''); setEditImageFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-bloom-text">{t('zk.upravitPribeh')}</DialogTitle>
            <DialogDescription>{t('zk.upravtePribeh')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 pt-2">
            <div>
              <Label>{t('akt.nadpisP')}</Label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} required data-testid="edit-story-title" />
            </div>
            <div>
              <Label>{t('akt.obsahP')}</Label>
              <RichTextEditor value={editContent} onChange={setEditContent} placeholder={t('zk.textPribehu')} />
            </div>
            <div>
              <Label>{t('zk.fazeTranzice')} <span className="text-bloom-sub font-normal text-xs">{t('pod.volitelne')}</span></Label>
              <Select value={editJourneyStage} onValueChange={setEditJourneyStage}>
                <SelectTrigger data-testid="edit-story-journey-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('zk.neuvadet')}</SelectItem>
                  {STAGES.map(st => <SelectItem key={st.id} value={st.id}>{t(st.klic)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-bloom-sub/70 mt-1">{t('zk.ukazeSeLidem')}</p>
            </div>
            <div>
              <Label>{t('akt.obrazekUrlNeboSoubor')}</Label>
              <Input value={editImageUrl} onChange={e => setEditImageUrl(e.target.value)} placeholder="https://..." />
              <input type="file" accept="image/*" onChange={e => setEditImageFile(e.target.files[0])} className="mt-1 text-xs" />
            </div>
            <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={uploading} data-testid="edit-story-submit">
              {t(uploading ? 'akt.ukladam' : 'pod.ulozitZmeny')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // ── Story list view ──────────────────────────────────────────────────────
  return (
    <>
    <div className="min-h-screen py-6" data-testid="stories-page">
      <div className="pride-bar mb-6" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          sectionKey="stories"
          klicTitulku="nav.stories"
          klicPodtitulku="zk.podtitulek"
          defaultColor={markerColor}
        />

        {/* Create story button */}
        {isAuthenticated && (
          <div className="mb-6">
            {canCreateStory ? (
              <Button onClick={() => setDialogOpen(true)} className="bg-bloom-violet text-white hover:bg-bloom-violet/90" data-testid="add-story-btn">
                <Plus className="w-4 h-4 mr-1.5" />{t('zk.sdiletPribeh')}
              </Button>
            ) : (
              <Link to="/profile" className="text-sm text-amber-600 hover:text-amber-700 font-medium">
                {t('zk.overEmail')}
              </Link>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : stories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-bloom-sub text-sm mb-3">{t('zk.zadnePribehy')}</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="stories-list">
            {stories.map(story => (
              <Card key={story.id} className="bg-white border-border/50 hover:border-bloom-pride-pink/40 transition-all cursor-pointer shadow-sm"
                onClick={() => navigate(`/zkusenosti/${story.id}`)} data-testid={`story-card-${story.id}`}>
                <CardContent className="p-0">
                  <div className="flex gap-4 items-start p-4">
                    {story.image_url && (
                      <img src={mediaUrl(story.image_url)} alt={story.title}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0"
                        onError={e => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: markerColor }} />
                        <span className="text-[10px] text-bloom-sub font-medium uppercase tracking-wide">{t('zk.pribeh')}</span>
                      </div>
                      <h2 className="font-serif font-semibold text-bloom-text text-base sm:text-lg leading-tight mb-2 line-clamp-2">
                        {story.title}
                      </h2>
                      <div className="flex items-center gap-2 text-xs text-bloom-sub">
                        <Link to={story.author_id ? `/users/${story.author_id}` : '#'} className="flex items-center gap-2 hover:text-bloom-violet transition-colors" onClick={e => e.stopPropagation()} data-testid={`story-author-link-${story.id}`}>
                          <Avatar className="w-5 h-5">
                            <AvatarImage src={getAvatarImage(story.admin_avatar, story.admin_custom_avatar)} />
                            <AvatarFallback className="bg-bloom-pride-pink/30 text-[8px]">
                              {(story.author_name || story.admin_name || 'A').charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{story.author_name || story.admin_name || t('zk.komunita')}</span>
                        </Link>
                        <Calendar className="w-3 h-3" />
                        <span>{datum(story.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* Create story dialog */}
    <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) { setTitle(''); setContent(''); setImageUrl(''); setImageFile(null); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-bloom-text">{t('zk.sdiletPribeh')}</DialogTitle>
          <DialogDescription>{t('akt.podelteSe')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4 pt-2">
          <div>
            <Label>{t('akt.nadpisP')}</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('zk.nazevPribehu')} required data-testid="create-story-title" />
          </div>
          <div>
            <Label>{t('zk.pribehP')}</Label>
            <RichTextEditor value={content} onChange={setContent} placeholder={t('zk.sdilejteZkusenost')} />
          </div>
          <div>
            <Label>{t('zk.fazeTranzice')} <span className="text-bloom-sub font-normal text-xs">{t('pod.volitelne')}</span></Label>
            <Select value={journeyStage} onValueChange={setJourneyStage}>
              <SelectTrigger data-testid="story-journey-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('zk.neuvadet')}</SelectItem>
                {STAGES.map(st => <SelectItem key={st.id} value={st.id}>{t(st.klic)}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-bloom-sub/70 mt-1">{t('zk.ukazeSeLidem')}</p>
          </div>
          <div>
            <Label>{t('akt.obrazekUrlNeboSoubor')}</Label>
            <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." />
            <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} className="mt-1 text-xs" />
          </div>
          <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={uploading} data-testid="create-story-submit">
            {t(uploading ? 'akt.nahravam' : 'zk.sdiletPribeh')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default StoriesPage;
